import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { BarChart3, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WithdrawalsComparativeStats } from './WithdrawalsComparativeStats';

interface WithdrawalsChartProps {
  withdrawals: any[];
  periodLabel?: string;
  customColor?: string;
  title?: string;
  chartType?: 'bar' | 'line';
  showComparativeStats?: boolean;
}

export const WithdrawalsChart: React.FC<WithdrawalsChartProps> = ({ 
  withdrawals, 
  periodLabel = '',
  customColor,
  title,
  chartType: initialChartType = 'bar',
  showComparativeStats = false
}) => {
  const [chartType, setChartType] = useState<'bar' | 'line'>(initialChartType);

  const chartData = useMemo(() => {
    const retraitsByDate = withdrawals.reduce((acc, retrait) => {
      const date = new Date(retrait.withdrawal_date).toLocaleDateString('fr-FR');
      acc[date] = (acc[date] || 0) + retrait.quantity_withdrawn;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(retraitsByDate)
      .sort(([a], [b]) => new Date(a.split('/').reverse().join('-')).getTime() - new Date(b.split('/').reverse().join('-')).getTime())
      .map(([date, quantity]) => ({ date, quantity }));
  }, [withdrawals]);

  return (
    <div className="space-y-6">
      <Card className="transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 
                className="w-5 h-5" 
                style={customColor ? { color: customColor } : undefined}
              />
              <span style={customColor ? { color: customColor } : undefined}>
                {title || `Évolution des retraits ${periodLabel}`}
              </span>
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant={chartType === 'bar' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setChartType('bar')}
                className="flex items-center gap-2"
              >
                <BarChart3 className="w-4 h-4" />
                Barres
              </Button>
              <Button
                variant={chartType === 'line' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setChartType('line')}
                className="flex items-center gap-2"
              >
                <TrendingUp className="w-4 h-4" />
                Courbe
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              {chartType === 'line' ? (
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(value) => `Date: ${value}`}
                    formatter={(value: any) => [value, 'Articles retirés']}
                  />
                  <Line 
                    type="monotone"
                    dataKey="quantity" 
                    stroke={customColor || "hsl(32 95% 60%)"} 
                    strokeWidth={3}
                    dot={{ fill: customColor || "hsl(32 95% 60%)", strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, fill: customColor || "hsl(32 95% 60%)" }}
                  />
                </LineChart>
              ) : (
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(value) => `Date: ${value}`}
                    formatter={(value: any) => [value, 'Articles retirés']}
                  />
                  <Bar 
                    dataKey="quantity" 
                    fill={customColor || "hsl(32 95% 60%)"} 
                    radius={4} 
                  />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {showComparativeStats && (
        <WithdrawalsComparativeStats withdrawals={withdrawals} customColor={customColor} />
      )}
    </div>
  );
};